/**
 * ============================================================================
 * Product Accordion Controller - Modern Event-Driven Version
 * ============================================================================
 *
 * Lightweight accordion system for custom product accordions
 * Supports: Main accordions, FAQ accordions, shipping accordions
 * Features: Smooth animations, FAQ search, keyboard navigation, performance monitoring
 *
 * Replaces: ACCORDION_CONTROLLER_SCRIPT (500+ lines → ~180 lines)
 *
 * PERFORMANCE MONITORING:
 * Automatically logs operation times in development mode (localhost/192.168.x.x)
 * - Accordion toggles: Logs if > 16.67ms (60fps threshold)
 * - FAQ search: Logs if > 50ms
 * - Check browser console for: [FAQ Performance] logs
 * - Green checkmarks (✓) = within threshold
 * - Yellow warnings = exceeds threshold
 *
 * THRESHOLDS:
 * - toggle: 16.67ms (60fps = 1000ms/60)
 * - search: 50ms (acceptable UI delay)
 *
 * ============================================================================
 */

(function () {
  'use strict';

  /* Configuration */
  const SELECTORS = {
    // Main product accordions
    accordionTrigger: '[data-accordion-trigger]',
    accordionContent: '[data-accordion-content]',

    // FAQ accordions (nested within main)
    faqTrigger: '[data-faq-trigger]',
    faqContent: '[data-faq-content]',
    faqBlock: '[data-faq-block]',
    faqList: '.faq-list',
    faqItem: '.faq-item',
    faqSearchInput: '[data-faq-search-input]',
    faqSearchClear: '[data-faq-search-clear]',
    faqSearchResults: '[data-faq-search-results]',

    // Shipping region accordions
    shippingTrigger: '[data-shipping-trigger]',
    shippingContent: '[data-shipping-content]',
  };

  // Animation constants (CSS handles animations, see faq-accordion.css)
  const ANIMATION = {
    duration: 350, // ms - Used for reference only
    easing: 'cubic-bezier(0.4, 0, 0.2, 1)', // Used for reference only
  };

  /* Performance Monitoring */
  const PERF_THRESHOLDS = {
    toggle: 16.67, // 60fps threshold (ms)
    search: 50, // Acceptable search delay (ms)
  };

  // Only enable performance logging in development mode
  const isDevelopment = window.location.hostname === 'localhost' ||
                        window.location.hostname.includes('127.0.0.1') ||
                        window.location.hostname.includes('192.168');

  /* Exclusive Accordion State */
  let exclusiveModeEnabled = true; // Only one FAQ can be open at a time

  /**
   * Log performance metrics if operation exceeds threshold
   */
  function logPerformance(operation, duration, threshold) {
    if (!isDevelopment) return;

    if (duration > threshold) {
      console.warn(`[FAQ Performance] ${operation} took ${duration.toFixed(2)}ms (threshold: ${threshold}ms)`);
    } else {
      console.log(`[FAQ Performance] ${operation}: ${duration.toFixed(2)}ms ✓`);
    }
  }

  /* Utility Functions */

  /**
   * Debounce utility for search input
   */
  const debounce = (fn, delay = 300) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), delay);
    };
  };

  /* Native <details> Animation */
  // Browser handles animation automatically - no custom slideOpen/slideClose needed!

  /**
   * Close all open FAQ accordions except the specified one (exclusive accordion behavior)
   * @param {HTMLDetailsElement} currentDetails - The details element that should stay open
   */
  function closeOtherAccordions(currentDetails) {
    if (!exclusiveModeEnabled) return;

    // Find all details elements in FAQ items
    const allDetails = document.querySelectorAll('.faq-item');

    allDetails.forEach((details) => {
      // Skip the current details element
      if (details === currentDetails) return;

      // Only close if FAQ's parent group is visible
      const faqGroup = details.closest('.faq-group');
      const isGroupVisible = faqGroup && faqGroup.style.display !== 'none';

      // Close if open and visible
      if (isGroupVisible && details.open) {
        details.removeAttribute('open');
      }
    });
  }

  /**
   * Handle native details toggle event
   * Only needs to manage exclusive accordion mode - browser handles animation
   */
  function handleDetailsToggle(event) {
    if (!(event.target instanceof HTMLDetailsElement)) return;
    if (!event.target.classList.contains('faq-item')) return;

    // Only act when opening (not closing)
    if (event.newState !== 'open') return;

    // Close other accordions when opening (exclusive accordion behavior)
    if (exclusiveModeEnabled) {
      closeOtherAccordions(event.target);
    }
  }

  /* FAQ Search Functionality */

  /**
   * Filter FAQ items based on search query
   */
  function performSearch(query, faqList, resultsContainer, resultsCount) {
    if (!faqList || !resultsContainer || !resultsCount) return;

    const startTime = performance.now();

    const normalizedQuery = (query || '').toLowerCase().trim();
    const faqItems = faqList.querySelectorAll(SELECTORS.faqItem);
    const faqGroups = faqList.querySelectorAll('.faq-group');

    // Show all FAQs and groups when query is empty
    if (!normalizedQuery) {
      faqItems.forEach(item => {
        item.style.display = '';
      });

      faqGroups.forEach(group => {
        group.style.display = '';
      });

      // ACCESSIBILITY FIX: Announce search cleared to screen readers
      const totalFAQs = faqItems.length;
      resultsCount.textContent = `Search cleared. Showing all ${totalFAQs} question${totalFAQs !== 1 ? 's' : ''}`;

      // Wait for screen reader announcement, then hide results container
      setTimeout(() => {
        resultsContainer.hidden = true;
        resultsCount.textContent = '';
      }, 1000);

      const duration = performance.now() - startTime;
      logPerformance('FAQ search (clear)', duration, PERF_THRESHOLDS.search);
      return;
    }

    let visibleCount = 0;

    // Filter FAQs based on question and answer text
    faqItems.forEach(item => {
      const question = item.querySelector(SELECTORS.faqTrigger);
      const answer = item.querySelector(SELECTORS.faqContent);

      const questionText = question ? question.textContent.toLowerCase() : '';
      const answerText = answer ? answer.textContent.toLowerCase() : '';

      if (questionText.includes(normalizedQuery) || answerText.includes(normalizedQuery)) {
        item.style.display = '';
        visibleCount++;
      } else {
        item.style.display = 'none';
      }
    });

    // Hide category groups that have no visible items
    faqGroups.forEach(group => {
      const visibleItemsInGroup = Array.from(group.querySelectorAll(SELECTORS.faqItem))
        .filter(item => item.style.display !== 'none');

      if (visibleItemsInGroup.length === 0) {
        group.style.display = 'none';
      } else {
        group.style.display = '';
      }
    });

    // Update results display
    resultsCount.textContent = `${visibleCount} result${visibleCount !== 1 ? 's' : ''} found`;
    resultsContainer.hidden = false;

    // Log performance with query info
    const duration = performance.now() - startTime;
    logPerformance(`FAQ search "${query}" (${faqItems.length} items)`, duration, PERF_THRESHOLDS.search);
  }

  /**
   * Attach search behaviour to FAQ blocks that render markup up-front
   */
  function initFAQBlock(block) {
    if (!block || block.getAttribute('data-faq-search-init') === 'true') return;

    const faqList = block.querySelector(SELECTORS.faqList);
    const searchInput = block.querySelector(SELECTORS.faqSearchInput);
    const clearButton = block.querySelector(SELECTORS.faqSearchClear);
    const resultsContainer = block.querySelector(SELECTORS.faqSearchResults);
    const resultsCount = resultsContainer ? resultsContainer.querySelector('.faq-results-count') : null;

    if (!faqList || !searchInput || !resultsContainer || !resultsCount) {
      block.setAttribute('data-faq-search-init', 'true');
      return;
    }

    const executeSearch = debounce(() => {
      performSearch(searchInput.value, faqList, resultsContainer, resultsCount);
    }, 300);

    searchInput.addEventListener('input', () => {
      const hasQuery = searchInput.value.trim().length > 0;
      if (clearButton) {
        clearButton.hidden = !hasQuery;
      }
      executeSearch();
    });

    if (clearButton) {
      clearButton.addEventListener('click', () => {
        searchInput.value = '';
        clearButton.hidden = true;
        performSearch('', faqList, resultsContainer, resultsCount);
        searchInput.focus();
      });
    }

    // Initialise default state
    performSearch('', faqList, resultsContainer, resultsCount);

    block.setAttribute('data-faq-search-init', 'true');
  }

  function initializeFAQSearch() {
    document.querySelectorAll(SELECTORS.faqBlock).forEach(initFAQBlock);
  }

  /* Event Handlers - Native details elements handle clicks/keyboard automatically! */

  /* Category Pills Navigation */

  /**
   * Filter FAQ groups by category
   */
  function filterByCategory(category, pills) {
    const groups = document.querySelectorAll('.faq-group');

    groups.forEach(group => {
      const groupCategory = group.getAttribute('data-faq-group');

      if (category === 'all') {
        // Show all groups
        group.style.display = '';
      } else {
        // Show only matching group
        const isMatch = groupCategory === category;
        group.style.display = isMatch ? '' : 'none';
      }
    });

    // Update active pill state and ARIA
    pills.forEach(pill => {
      const pillCategory = pill.getAttribute('data-category');
      const isActive = pillCategory === category;

      if (isActive) {
        pill.classList.add('active');
        pill.setAttribute('aria-pressed', 'true');
      } else {
        pill.classList.remove('active');
        pill.setAttribute('aria-pressed', 'false');
      }
    });
  }

  /**
   * Initialize category pills navigation
   */
  function initializeCategoryPills() {
    const navContainers = document.querySelectorAll('[data-faq-category-nav]');

    navContainers.forEach(nav => {
      if (nav.getAttribute('data-pills-init') === 'true') return;

      const pills = nav.querySelectorAll('[data-faq-pill]');

      if (pills.length === 0) return;

      // Add click handlers to pills
      pills.forEach(pill => {
        pill.addEventListener('click', () => {
          const category = pill.getAttribute('data-category');
          filterByCategory(category, pills);

          // Scroll pill into view if partially hidden
          pill.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        });
      });

      nav.setAttribute('data-pills-init', 'true');
    });
  }

  /* Expand/Collapse All Controls */

  /**
   * Expand all FAQ accordions (only visible ones)
   * Temporarily disables exclusive accordion mode
   */
  function expandAll() {
    // Disable exclusive mode to allow multiple FAQs open
    exclusiveModeEnabled = false;

    const allDetails = document.querySelectorAll('.faq-item');

    allDetails.forEach((details) => {
      // Check if the FAQ's parent group is visible
      const faqGroup = details.closest('.faq-group');
      const isGroupVisible = faqGroup && faqGroup.style.display !== 'none';

      // Only expand if the group is visible and accordion is closed
      if (isGroupVisible && !details.open) {
        details.setAttribute('open', '');
      }
    });

    // Re-enable exclusive mode immediately (no animation delays needed)
    setTimeout(() => {
      exclusiveModeEnabled = true;
    }, 100);
  }

  /**
   * Collapse all FAQ accordions (only visible ones)
   */
  function collapseAll() {
    // Disable exclusive mode to allow operating on all FAQs
    exclusiveModeEnabled = false;

    const allDetails = document.querySelectorAll('.faq-item');

    allDetails.forEach((details) => {
      // Check if the FAQ's parent group is visible
      const faqGroup = details.closest('.faq-group');
      const isGroupVisible = faqGroup && faqGroup.style.display !== 'none';

      // Only collapse if the group is visible and accordion is open
      if (isGroupVisible && details.open) {
        details.removeAttribute('open');
      }
    });

    // Re-enable exclusive mode immediately (no animation delays needed)
    setTimeout(() => {
      exclusiveModeEnabled = true;
    }, 100);
  }

  /**
   * Initialize Expand/Collapse All controls
   */
  function initializeExpandCollapseControls() {
    const controls = document.querySelectorAll('[data-faq-controls]');

    controls.forEach(control => {
      if (control.getAttribute('data-controls-init') === 'true') return;

      const expandBtn = control.querySelector('[data-faq-expand-all]');
      const collapseBtn = control.querySelector('[data-faq-collapse-all]');

      if (!expandBtn || !collapseBtn) return;

      // Expand all handler
      expandBtn.addEventListener('click', () => {
        expandAll();
        expandBtn.disabled = true;
        setTimeout(() => {
          expandBtn.disabled = false;
        }, 1000); // Prevent spam clicking
      });

      // Collapse all handler
      collapseBtn.addEventListener('click', () => {
        collapseAll();
        collapseBtn.disabled = true;
        setTimeout(() => {
          collapseBtn.disabled = false;
        }, 1000); // Prevent spam clicking
      });

      control.setAttribute('data-controls-init', 'true');
    });
  }

  /* Initialization */

  /**
   * Initialize accordion system with error boundary
   */
  function init() {
    try {
      // Wire FAQ search inputs
      initializeFAQSearch();

      // Initialize category pills navigation
      initializeCategoryPills();

      // Initialize Expand/Collapse All controls
      initializeExpandCollapseControls();

      // FAQ exclusive mode: Only one FAQ can be open at a time
      // Note: accordion-custom's :scope > details selector doesn't work for nested FAQ structure
      // So we handle exclusive mode here with a global toggle listener
      document.addEventListener('toggle', handleDetailsToggle, true);

      // Re-initialise search, pills, and controls when sections are re-rendered in the editor
      document.addEventListener('shopify:section:load', () => {
        initializeFAQSearch();
        initializeCategoryPills();
        initializeExpandCollapseControls();
      });
      document.addEventListener('shopify:block:select', () => {
        initializeFAQSearch();
        initializeCategoryPills();
        initializeExpandCollapseControls();
      });

      console.log('[Product Accordion] Initialized - Native <details> version (lightweight & smooth!)');
    } catch (error) {
      // Error boundary: Log error but don't break page functionality
      console.error('[Product Accordion] Initialization failed:', error);
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
